import { useEffect, useState } from 'react'
import { Button, ErrorBanner } from '../../components/forms'
import { useRequestApprovalCode, type Approver } from '../../features/pos/api'

/**
 * "Text them a code", and what came back (D-240).
 *
 * One control for all three places a till asks somebody to approve something — a discount, a credit
 * limit, a held unit — because they are the same question and a cashier should not meet three
 * different answers to it.
 *
 * The code never passes through here. The server texts it and tells us only who it went to and the
 * last four digits of the number, which is what the cashier needs in order to say who to chase.
 */
export function SendApprovalCode({
  locationId,
  approver,
  purpose,
  discountPercent,
  saleValue,
}: {
  locationId: string
  /** The chosen approver, or undefined while nobody is chosen. */
  approver: Approver | undefined
  purpose: 'Discount' | 'PriceOverride' | 'CreditOverride' | 'VatWaiver' | 'HoldRelease'
  discountPercent?: number
  saleValue?: number
}) {
  const send = useRequestApprovalCode()
  const [sent, setSent] = useState<{ approverName: string; phoneHint: string } | null>(null)

  // A code sent to one person does not approve for another, so changing the approver clears the
  // "we texted…" line rather than leaving it pointing at the wrong name.
  useEffect(() => {
    setSent(null)
    send.reset()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [approver?.id])

  if (!approver) return null

  if (!approver.canBeTexted) {
    return (
      <p className="text-xs text-dpl-muted">
        {approver.name} has no phone number on file, so no code can be sent. Add one under Users, or use their PIN.
      </p>
    )
  }

  return (
    <div className="grid gap-2">
      <Button
        type="button"
        tone="secondary"
        busy={send.isPending}
        onClick={() =>
          send.mutate(
            { locationId, approverUserId: approver.id, purpose, discountPercent, saleValue },
            { onSuccess: (r) => setSent({ approverName: r.approverName, phoneHint: r.phoneHint }) },
          )
        }
      >
        {sent ? 'Send another code' : 'Text them a code'}
      </Button>
      {sent && (
        <p className="text-xs text-dpl-muted" role="status">
          Sent to {sent.approverName} on …{sent.phoneHint}. Good for 10 minutes. Ask them to read the code back.
        </p>
      )}
      <ErrorBanner error={send.error} />
    </div>
  )
}
